import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Clock, User, Eye, Info, ArrowLeft } from "lucide-react";
import { api } from "@/api";
import { useLang } from "@/i18n";
import Markdown from "@/components/Markdown";
import Seo from "@/components/Seo";
import { NewsletterBox } from "@/components/NewsletterBox";

export default function ArticleDetail() {
  const { slug } = useParams();
  const { lang, setLang, t } = useLang();
  const [params] = useSearchParams();
  const [article, setArticle] = useState(null);
  const [progress, setProgress] = useState(0);

  // honor ?lang= for crawlers / hreflang deep links
  useEffect(() => {
    const q = params.get("lang");
    if (q && (q === "it" || q === "en") && q !== lang) setLang(q);
  }, [params]); // eslint-disable-line

  useEffect(() => {
    setArticle(null);
    api.get(`/articles/${slug}`, { params: { lang } }).then((r) => setArticle(r.data)).catch(() => setArticle(false));
  }, [slug, lang]);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const scrolled = (h.scrollTop / (h.scrollHeight - h.clientHeight)) * 100;
      setProgress(scrolled);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (article === null)
    return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-zinc-500">Loading…</div>;
  if (article === false)
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <p className="text-zinc-400">Article not found.</p>
        <Link to="/articles" className="text-[#E63946] font-semibold">← {t("nav_articles")}</Link>
      </div>
    );

  return (
    <div>
      <Seo
        title={article.seo_title || article.title}
        description={article.seo_description || article.excerpt}
        image={article.cover_image}
        type="article"
        alternates={(article.available_langs || []).map((l) => ({
          lang: l,
          url: `${window.location.origin}/article/${slug}?lang=${l}`,
        }))}
      />
      <div className="reading-progress" style={{ width: `${progress}%` }} />

      <div className="relative aspect-[21/9] max-h-[420px] overflow-hidden">
        <img src={article.cover_image} alt={article.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0c0d10] via-[#0c0d10]/40 to-transparent" />
      </div>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 -mt-24 relative">
        <Link to="/articles" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-4">
          <ArrowLeft size={15} /> {t("nav_articles")}
        </Link>
        <span
          className="inline-block px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-white rounded bg-[#E63946]"
        >
          {article.category}
        </span>
        <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-white leading-tight">
          {article.title}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-zinc-500 font-mono border-b border-[#272b35] pb-6">
          <span className="inline-flex items-center gap-1.5"><User size={13} /> {t("by")} {article.author}</span>
          <span className="inline-flex items-center gap-1.5"><Clock size={13} /> {article.read_minutes} {t("min_read")}</span>
          <span className="inline-flex items-center gap-1.5"><Eye size={13} /> {article.views} {t("views")}</span>
        </div>

        {article.affiliate_disclaimer && (
          <div
            data-testid="affiliate-disclaimer"
            className="mt-6 flex items-start gap-3 bg-[#14161b] border border-[#272b35] rounded-lg p-4 text-xs text-zinc-400"
          >
            <Info size={16} className="text-[#F59E0B] shrink-0 mt-0.5" />
            <p>{t("affiliate_note")}</p>
          </div>
        )}

        <div className="mt-8">
          <Markdown content={article.content} />
        </div>

        <div className="mt-14 mb-10">
          <NewsletterBox variant="article" />
        </div>
      </article>
    </div>
  );
}
